import IRepository from 'data/IRepository';
import { Commodity } from 'features/shared/enums/commodity';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import { trackPromise } from 'react-promise-tracker';
import ActionResultDTO from 'features/shared/dtos/ActionResultDTO';
import { Contracting_AddContracting } from 'data/endpoints';
import { useSelector } from 'react-redux';
import IAppState from 'infrastructure/store/IAppState';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import { ContractingDTO } from 'features/contracting/dtos/ContractingDTO';
import { createDefaultContracting } from 'features/contracting/helpers/contractingFormHelper';
import { loadModule } from 'features/shared/helpers/modules';
import ContractingStepConfiguration from 'features/contracting/models/ContractingStepConfiguration';
import { getQueryParams } from 'features/shared/helpers/queryParams';
import CustomerTypeStep from 'features/contracting/models/steps/customerType/CustomerTypeStep';
import ContractingStepRoute from 'features/contracting/models/ContractingStepRoute';
import ContractingSteps from 'features/contracting/models/ContractingSteps';
import { useHistory } from 'react-router-dom';
import { useContractingDialog } from './useContractingDialog';
import { isSuccessStatus } from 'data/helpers/responseHelper';
import { AlertType } from 'features/shared/alerts/enums/alertType';
import { getContractCategory } from 'features/contracting/sections/contractingForms/customerTypeForm/customerTypeFormHelper';
import CustomerDataStep from 'features/contracting/models/steps/customerData/CustomerDataStep';
import ContractingStepData from 'features/contracting/models/ContractingStepData';

// config
const contractingSteps = loadModule<ContractingStepConfiguration[]>(
  "contracting/contractingSteps.json"
);

// models
export interface ContractingAddHookResult {
  onAddClick: (commodity: Commodity) => Promise<void>;
  onAddCompanyClick: (commodity: Commodity, customerData: CustomerDataStep) => Promise<void>;
}

export const useContractingAdd = (routePrefix: string): ContractingAddHookResult => {
  // DI
  const contractingRepository = useInjection<IRepository<ContractingDTO>>(
    IocTypes.Repository
  );

  // router
  const history = useHistory();

  // store
  const customer = useSelector<IAppState, CustomerState>((store) => store.customer).customer;

  // info dialog
  const { openInfoDialog, closeInfoDialog } = useContractingDialog();

  // handlers
  const addContractingHandler = async (commodity: Commodity) => {
    // company - must fill authorized person (redirect to prepare page)
    if (customer.customerType === "Company") {
      history.push(`/contracting/prepare/${commodity}`);
      return;
    }
    // person - add immediately 
    else {
      const stepsData: ContractingSteps = {
        customerType: getCustomerTypeStep(commodity)
      };
      await addContracting(stepsData);
    }
  }

  const addCompanyContractingHandler = async (commodity: Commodity, customerData: CustomerDataStep) => {
    const stepsData: ContractingSteps = {
      customerType: getCustomerTypeStep(commodity),
      customerData: {
        touched: true,
        valid: false,
        disabled: false,
        values: customerData
      }
    };

    await addContracting(stepsData);
  }

  // helpers
  const getCustomerTypeStep = (commodity: Commodity): ContractingStepData => { 
    return {
      touched: true,
      valid: true,
      disabled: true,
      values: {
        commodity: commodity,
        customerType: customer.customerType,
        contractCategoryCode: getContractCategory( customer.customerType!, commodity)
      } as CustomerTypeStep
    }
  }

  const addContracting = async (steps: ContractingSteps) => { 
    const route: ContractingStepRoute = {
      routePrefix: routePrefix,
      step: "paymentMethod"
    };

    const contracting = createDefaultContracting(contractingSteps, route, steps);
    
    const response = await trackPromise(
      contractingRepository.post<ActionResultDTO<string>>({
        url: Contracting_AddContracting,
        data: contracting,
        params: getQueryParams("CustomerId", customer.customerId!)
      })
    );

    if (isSuccessStatus(response) && response.data) {
      // move to payment step (after customer data)
      history.push({
        pathname: routePrefix,
        search: getQueryParams("contractingId", response.data.result).toString()
      });
    } else {
      // show error dialog
      openInfoDialog("addErrorTitle", {
        type: AlertType.Danger,
        message: "addErrorMessage",
        primaryBtn: {
          text: "close",
          action: () => closeInfoDialog(),
        },
      });
    }
  }

  // result
  return {
    onAddClick: addContractingHandler,
    onAddCompanyClick: addCompanyContractingHandler
  };
}